import { SceneKey } from '@models/scene-keys';
import { ApiService } from '@services/api/ApiService';
import { GameConfigService } from '@services/core/GameConfigService';
import Phaser from 'phaser';
import { INITIAL_HEIGHT, INITIAL_WIDTH, DPR } from '@constants/common.const';
import { ALL_SCENES } from './scenes';
import KawaseBlurPipelinePlugin from 'phaser3-rex-plugins/plugins/kawaseblurpipeline-plugin.js';
import { registerGlobalErrorHandler } from '@errors/global-error-handler';
import { initSentry } from './sentry/init-sentry';
import { getSessionParams } from '@utils/get-url-params';

class Boot extends Phaser.Scene {
  constructor() {
    super('Boot');
  }

  preload() {
    this.load.pack('pack', 'preload-asset-pack.json');
  }

  create() {
    const configService = new GameConfigService();
    const isAdmin = configService.sessionParams?.isAdmin ?? false;
    this.scene.start(isAdmin ? SceneKey.AdminPreload : SceneKey.Preload);
  }
}

const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// Function to get current viewport dimensions
const getViewportSize = () => {
  if (isMobile()) {
    // Use visualViewport API if available (better for mobile)
    if (window.visualViewport) {
      return {
        width: window.visualViewport.width * DPR,
        height: window.visualViewport.height * DPR,
      };
    }
    // Fallback to window.innerWidth/innerHeight
    return {
      width: window.innerWidth * DPR,
      height: window.innerHeight * DPR,
    };
  }
  return {
    width: INITIAL_WIDTH * DPR,
    height: INITIAL_HEIGHT * DPR,
  };
};

/**
 * Ensure the Korolev webfont is available before Phaser starts creating Text objects.
 *
 * Typekit CSS is already included in `index.html`, this just waits until the browser
 * confirms the font is ready.
 */
async function loadFonts() {
  await Promise.all([
    document.fonts.load('500 16px "korolev"'),
    document.fonts.load('700 16px "korolev"'),
    document.fonts.load('900 16px "korolev"'),
  ]);

  await document.fonts.ready;

  // sanity check
  if (!document.fonts.check('16px "korolev"')) {
    console.warn('Korolev font is still not available.');
  }
}

window.addEventListener('load', async function () {
  initSentry();
  registerGlobalErrorHandler();

  const configService = new GameConfigService();
  const api = new ApiService();

  const params = getSessionParams();
  configService.setSessionParams(params);
  api.setToken(params.token);

  if (!params.isAdmin) {
    if (!params.gameUuid) {
      const container = document.getElementById('game-container');
      if (container) {
        container.style.display = 'flex';
        container.style.alignItems = 'center';
        container.style.justifyContent = 'center';
        container.style.fontSize = '24px';
        container.textContent = 'There is no game uuid';
      }
      return;
    }
    // fetchGameConfigUrl returns a never-resolving promise on opaqueredirect,
    // so Phaser will not start if the user needs to log in.
    const gameResponse = await api.fetchGameConfigUrl(params);
    console.log(`Game response: ${gameResponse} params ${params}`);
    configService.setGameResponse(gameResponse);
  }

  await loadFonts();

  const viewportSize = getViewportSize();
  const width = viewportSize.width;
  const height = viewportSize.height;

  const game = new Phaser.Game({
    type: Phaser.WEBGL,
    width,
    height,
    backgroundColor: '#2f2f2f',
    parent: 'game-container',
    scene: [Boot, ...ALL_SCENES],
    plugins: {
      global: [
        {
          key: 'rexKawaseBlurPipeline',
          plugin: KawaseBlurPipelinePlugin,
          start: true,
        },
      ],
    },
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width,
      height,
      zoom: 1 / DPR,
    },
    dom: { createContainer: true },
    render: {
      antialias: true, // CRITICAL: Makes graphics/shapes smooth
      pixelArt: false, // CRITICAL: Ensure this is FALSE for UI games
      roundPixels: false, // Optional: Set false allows sub-pixel rendering (smoother motion)
    },
  });

  // Handle viewport resize on mobile (for address bar show/hide)
  if (isMobile()) {
    const handleResize = () => {
      const newSize = getViewportSize();
      game.scale.resize(newSize.width, newSize.height);
    };

    const handleOrientationChange = () => {
      setTimeout(handleResize, 100); // Small delay to ensure proper dimensions
    };

    // Listen to both window resize and visualViewport resize
    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    }

    // Handle orientation change
    window.addEventListener('orientationchange', handleOrientationChange);

    // Optional: Clean up listeners on page unload (good practice)
    window.addEventListener('beforeunload', () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      }
    });
  }
});
